import { useState, useEffect, useCallback } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '@/lib/supabase';

type ReportType = 'teams' | 'participants' | 'college' | 'department' | 'domain' | 'payment' | 'faculty';
type ExportFormat = 'pdf' | 'excel' | 'csv';

const reportTypes: { key: ReportType; label: string; icon: string; desc: string }[] = [
  { key: 'teams', label: 'Teams Report', icon: 'ri-file-list-3-line', desc: 'Complete list of all team registrations' },
  { key: 'participants', label: 'Participants Report', icon: 'ri-user-line', desc: 'All participants with college and department details' },
  { key: 'college', label: 'College-wise Report', icon: 'ri-building-line', desc: 'Team distribution across colleges' },
  { key: 'department', label: 'Department-wise Report', icon: 'ri-stack-line', desc: 'Participant distribution across departments' },
  { key: 'domain', label: 'Domain-wise Report', icon: 'ri-pie-chart-line', desc: 'Problem statement selections by domain' },
  { key: 'payment', label: 'Payments Report', icon: 'ri-bank-card-line', desc: 'All payment transactions with status' },
  { key: 'faculty', label: 'Faculty Report', icon: 'ri-user-star-line', desc: 'All faculty mentors and assigned teams' },
];

const exportFormats: { key: ExportFormat; label: string; icon: string }[] = [
  { key: 'csv', label: 'CSV', icon: 'ri-file-text-line' },
  { key: 'excel', label: 'Excel', icon: 'ri-file-excel-line' },
  { key: 'pdf', label: 'PDF', icon: 'ri-file-pdf-line' },
];

function downloadCSV(filename: string, headers: string[], rows: string[][]) {
  const csvContent = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function getDateStr() {
  return new Date().toISOString().split('T')[0];
}

export default function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState<ReportType | ''>('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [downloadMsg, setDownloadMsg] = useState('');

  const fetchReport = useCallback(async () => {
    if (!selectedReport) return;
    setLoading(true);
    setReportData(null);
    setDownloadMsg('');

    try {
      let headers: string[] = [];
      let rows: string[][] = [];

      switch (selectedReport) {
        case 'teams': {
          const { data } = await supabase.from('teams').select('registration_id,team_name,college_name,department,city,team_email,status,created_at').order('created_at', { ascending: false });
          headers = ['Registration ID', 'Team Name', 'College', 'Department', 'City', 'Email', 'Status', 'Registered On'];
          rows = (data || []).map((t) => [t.registration_id, t.team_name, t.college_name, t.department, t.city, t.team_email, t.status, new Date(t.created_at).toLocaleDateString('en-IN')]);
          break;
        }
        case 'participants': {
          const { data: p } = await supabase.from('participants').select('full_name,email,contact_number,department,year_of_study,course_type,team_id,is_leader').order('full_name');
          const { data: t } = await supabase.from('teams').select('id,team_name,college_name');
          const teamMap: Record<string, string> = {};
          (t || []).forEach((tm) => { teamMap[tm.id] = `${tm.team_name} (${tm.college_name})`; });
          headers = ['Name', 'Email', 'Contact', 'Department', 'Year', 'Course Type', 'Team', 'Role'];
          rows = (p || []).map((pp) => [pp.full_name, pp.email, pp.contact_number, pp.department, pp.year_of_study, pp.course_type, teamMap[pp.team_id] || '', pp.is_leader ? 'Team Leader' : 'Member']);
          break;
        }
        case 'college': {
          const { data } = await supabase.from('teams').select('college_name').order('college_name');
          const counts: Record<string, number> = {};
          (data || []).forEach((t) => { counts[t.college_name] = (counts[t.college_name] || 0) + 1; });
          headers = ['College Name', 'Team Count'];
          rows = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([n, c]) => [n, String(c)]);
          break;
        }
        case 'department': {
          const { data } = await supabase.from('participants').select('department').order('department');
          const counts: Record<string, number> = {};
          (data || []).forEach((p) => { counts[p.department] = (counts[p.department] || 0) + 1; });
          headers = ['Department', 'Participant Count'];
          rows = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([n, c]) => [n, String(c)]);
          break;
        }
        case 'domain': {
          const { data: ps } = await supabase.from('problem_statements').select('id,title,domain_id').order('title');
          const { data: domains } = await supabase.from('domains').select('id,name');
          const { data: teams } = await supabase.from('teams').select('selected_problem_statement_id').not('selected_problem_statement_id', 'is', null);
          
          const domainMap: Record<string, string> = {};
          (domains || []).forEach((d) => { domainMap[d.id] = d.name; });
          
          const teamCounts: Record<string, number> = {};
          (teams || []).forEach((t) => {
            const psid = t.selected_problem_statement_id!;
            teamCounts[psid] = (teamCounts[psid] || 0) + 1;
          });
          
          headers = ['Problem Statement', 'Domain', 'Team Count'];
          rows = (ps || []).map((p) => {
            const domainName = domainMap[p.domain_id || ''] || 'Unknown';
            return [p.title, domainName, String(teamCounts[p.id] || 0)];
          }).sort((a, b) => a[1].localeCompare(b[1]) || a[0].localeCompare(b[0]));
          break;
        }
        case 'payment': {
          const { data: p } = await supabase.from('payments').select('razorpay_order_id,razorpay_payment_id,amount,status,team_id,created_at').order('created_at', { ascending: false });
          const { data: t } = await supabase.from('teams').select('id,team_name,registration_id');
          const teamMap: Record<string, string> = {};
          (t || []).forEach((tm) => { teamMap[tm.id] = `${tm.registration_id} - ${tm.team_name}`; });
          headers = ['Order ID', 'Payment ID', 'Amount', 'Status', 'Team', 'Date'];
          rows = (p || []).map((pp) => [pp.razorpay_order_id || '', pp.razorpay_payment_id || '', `₹${(pp.amount || 0) / 100}`, pp.status, teamMap[pp.team_id] || '', new Date(pp.created_at).toLocaleDateString('en-IN')]);
          break;
        }
        case 'faculty': {
          const { data: f } = await supabase.from('faculty_mentors').select('name,designation,department,email,contact_number,team_id,created_at').order('created_at', { ascending: false });
          const { data: t } = await supabase.from('teams').select('id,team_name');
          const teamMap: Record<string, string> = {};
          (t || []).forEach((tm) => { teamMap[tm.id] = tm.team_name; });
          headers = ['Name', 'Designation', 'Department', 'Email', 'Contact', 'Team', 'Added On'];
          rows = (f || []).map((ff) => [ff.name, ff.designation, ff.department, ff.email, ff.contact_number, teamMap[ff.team_id] || '', new Date(ff.created_at).toLocaleDateString('en-IN')]);
          break;
        }
      }

      setReportData({ headers, rows });
    } catch {
      setDownloadMsg('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedReport]);

  useEffect(() => {
    if (selectedReport) fetchReport();
  }, [selectedReport, fetchReport]);

  const handleDownload = (format: ExportFormat) => {
    if (!reportData || !selectedReport) return;
    const date = getDateStr();
    const label = reportTypes.find((r) => r.key === selectedReport)?.label || selectedReport;
    const filename = `${label.replace(/\s+/g, '_')}_${date}.csv`;

    if (format === 'csv') {
      downloadCSV(filename, reportData.headers, reportData.rows);
      setDownloadMsg(`CSV downloaded: ${filename}`);
      setTimeout(() => setDownloadMsg(''), 3000);
      return;
    }

    if (format === 'excel') {
      // Excel-compatible CSV with BOM
      downloadCSV(filename.replace('.csv', '.xls'), reportData.headers, reportData.rows);
      setDownloadMsg(`Excel file downloaded`);
      setTimeout(() => setDownloadMsg(''), 3000);
      return;
    }

    if (format === 'pdf') {
      // Use browser print for PDF
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setDownloadMsg('Please allow popups for PDF export');
        return;
      }
      printWindow.document.write(`<html><head><title>${label} - ${date}</title><style>body{font-family:Arial,sans-serif;padding:20px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:8px;text-align:left;font-size:12px}th{background:#f5f5f5;font-weight:bold}h2{color:#333}</style></head><body><h2>${label}</h2><p>Generated: ${date}</p><table><thead><tr>${reportData.headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${reportData.rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody></table></body></html>`);
      printWindow.document.close();
      printWindow.print();
      setDownloadMsg('PDF printing...');
      setTimeout(() => setDownloadMsg(''), 3000);
    }
  };

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="Reports" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Reports' }]}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {reportTypes.map((r) => (
            <button
              key={r.key}
              onClick={() => setSelectedReport(r.key === selectedReport ? '' : r.key)}
              className={`text-left p-5 rounded-xl border transition-all cursor-pointer ${
                selectedReport === r.key
                  ? 'border-primary-400 bg-primary-50/50'
                  : 'border-background-200/70 bg-background-50 hover:bg-background-50/70'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                selectedReport === r.key ? 'bg-primary-100 text-primary-600' : 'bg-background-100 text-foreground-500'
              }`}>
                <i className={`${r.icon} text-lg`}></i>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">{r.label}</h3>
              <p className="text-xs text-foreground-400">{r.desc}</p>
            </button>
          ))}
        </div>

        {selectedReport && (
          <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                {reportTypes.find((r) => r.key === selectedReport)?.label}
              </h3>
              <button onClick={fetchReport} className="px-3 py-1.5 rounded-lg border border-background-200 text-foreground-600 text-xs hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-refresh-line mr-1"></i>Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : reportData ? (
              <>
                <div className="overflow-x-auto max-h-72 overflow-y-auto mb-4 border border-background-200/70 rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200/70 bg-background-100/50 sticky top-0">
                        {reportData.headers.map((h) => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.rows.map((row, i) => (
                        <tr key={i} className="border-b border-background-100 hover:bg-background-50/50">
                          {row.map((cell, j) => (
                            <td key={j} className="px-3 py-2 text-xs text-foreground-600 whitespace-nowrap">{cell}</td>
                          ))}
                        </tr>
                      ))}
                      {reportData.rows.length === 0 && (
                        <tr><td colSpan={reportData.headers.length} className="px-4 py-8 text-center text-foreground-400 text-xs">No data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {exportFormats.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => handleDownload(f.key)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 border border-background-200 hover:bg-background-100"
                    >
                      <i className={f.icon}></i>
                      Export {f.label}
                    </button>
                  ))}
                </div>
                {downloadMsg && (
                  <p className="text-xs text-accent-600 mt-3">{downloadMsg}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-foreground-400 py-8 text-center">Click Refresh to load data</p>
            )}
          </div>
        )}

        {!selectedReport && (
          <div className="py-20 text-center text-foreground-400">
            <i className="ri-file-chart-line text-5xl block mb-3"></i>
            <p className="text-sm">Select a report type above to generate and export data</p>
          </div>
        )}
      </AdminLayout>
    </AuthGuard>
  );
}